// ============================================================================
// AIRS Security - FleetBehaviorPage.js
// Enterprise UEBA Fleet Analytics Dashboard
// Ready-to-replace file
// File:
// /media/serverdata/projects/command-center/frontend/src/features/dashboard/FleetBehaviorPage.js
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosClient from '../../api/axiosClient';
import {
  AlertTriangle,
  Smartphone,
  Laptop,
  Shield,
  Cpu,
  MemoryStick,
  RefreshCw,
  Server,
} from "lucide-react";


const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 B";

  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

const getOSIcon = (os) => {
  const normalized = (os || "").toLowerCase();

  if (normalized.includes("android")) {
    return <Smartphone size={18} className="text-green-400" />;
  }

  if (normalized.includes("windows")) {
    return <Laptop size={18} className="text-blue-400" />;
  }

  return <Server size={18} className="text-orange-400" />;
};

const getRiskBadge = (score) => {
  const abs = Math.abs(score || 0);

  if (abs >= 0.7) {
    return {
      label: "CRITICAL",
      className: "bg-red-500/20 text-red-400 border border-red-500/30",
    };
  }

  if (abs >= 0.4) {
    return {
      label: "HIGH",
      className: "bg-orange-500/20 text-orange-400 border border-orange-500/30",
    };
  }

  if (abs >= 0.2) {
    return {
      label: "MEDIUM",
      className: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30",
    };
  }

  return {
    label: "NORMAL",
    className: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
  };
};

export default function FleetBehaviorPage() {
  const [loading, setLoading] = useState(true);
  const [fleetData, setFleetData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [filters, setFilters] = useState({
    os: "all",
    anomalousOnly: false,
  });

  const fetchFleetData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axiosClient.get(
        `/admin/telemetry/anomalies?limit=200`
      );

      //console.log("[UEBA-RAW-RESPONSE]", response);

      const raw = response;

      const data = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
          ? raw.results
          : [];

      console.log("[UEBA-FINAL-DATA]", data);

      setFleetData(data);

      if (!selectedAgent && data.length > 0) {
        setSelectedAgent(data[0].agent_id);
      }
    } catch (err) {
      console.error("[UEBA] Failed to fetch telemetry:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgent]);

  useEffect(() => {
    fetchFleetData();

    const interval = setInterval(fetchFleetData, 30000);

    return () => clearInterval(interval);
  }, [fetchFleetData]);

  const filteredFleet = useMemo(() => {
    return fleetData.filter((row) => {
      if (
        filters.os !== "all" &&
        (row.os_type || "").toLowerCase() !== filters.os
      ) {
        return false;
      }

      if (filters.anomalousOnly && !row.is_anomalous) {
        return false;
      }

      return true;
    });
  }, [fleetData, filters]);

  const groupedAgents = useMemo(() => {
    const map = {};

    filteredFleet.forEach((row) => {
      if (!map[row.agent_id]) {
        map[row.agent_id] = {
          agent_id: row.agent_id,
          hostname: row.hostname || "Unknown Device",
          os_type: row.os_type || "linux",
          status: row.status || "online",
          rows: [],
          highestScore: 0,
        };
      }

      map[row.agent_id].rows.push(row);

      const current = Math.abs(row.ml_anomaly_score || 0);

      if (current > map[row.agent_id].highestScore) {
        map[row.agent_id].highestScore = current;
      }
    });

    return Object.values(map).sort(
      (a, b) => b.highestScore - a.highestScore
    );
  }, [filteredFleet]);

  const selectedAgentData = useMemo(() => {
    return groupedAgents.find(
      (x) => x.agent_id === selectedAgent
    );
  }, [groupedAgents, selectedAgent]);

  const fleetSummary = useMemo(() => {
    return {
      devices: groupedAgents.length,
      anomalies: filteredFleet.filter((x) => x.is_anomalous).length,
      critical: filteredFleet.filter(
        (x) => Math.abs(x.ml_anomaly_score || 0) >= 0.7
      ).length,
    };
  }, [groupedAgents, filteredFleet]);

  return (
    <div className="min-h-screen bg-[#060816] text-white p-6">
      {/* HEADER */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="text-cyan-400" size={28} />

          <h1 className="text-3xl font-bold">
            Behavioral Analytics (UEBA)
          </h1>
        </div>

        <p className="text-gray-400">
          AI-driven anomaly detection across your fleet with
          agent-level behavioral telemetry intelligence.
        </p>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#0e1428] border border-cyan-500/20 rounded-2xl p-5">
          <div className="text-gray-400 text-sm mb-2">
            Fleet Devices
          </div>

          <div className="text-3xl font-bold">
            {fleetSummary.devices}
          </div>
        </div>

        <div className="bg-[#0e1428] border border-orange-500/20 rounded-2xl p-5">
          <div className="text-gray-400 text-sm mb-2">
            Active Anomalies
          </div>

          <div className="text-3xl font-bold text-orange-400">
            {fleetSummary.anomalies}
          </div>
        </div>

        <div className="bg-[#0e1428] border border-red-500/20 rounded-2xl p-5">
          <div className="text-gray-400 text-sm mb-2">
            Critical Risk Devices
          </div>

          <div className="text-3xl font-bold text-red-400">
            {fleetSummary.critical}
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-[#0e1428] border border-cyan-500/10 rounded-2xl p-4 mb-6 flex flex-wrap gap-3 items-center">
        <button
          onClick={fetchFleetData}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 transition"
        >
          <RefreshCw size={16} />
          Refresh
        </button>

        <select
          value={filters.os}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              os: e.target.value,
            }))
          }
          className="bg-[#11192f] border border-cyan-500/20 rounded-xl px-4 py-2"
        >
          <option value="all">All Platforms</option>
          <option value="linux">Linux</option>
          <option value="windows">Windows</option>
          <option value="android">Android</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={filters.anomalousOnly}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                anomalousOnly: e.target.checked,
              }))
            }
          />

          Show anomalous devices only
        </label>

        <div className="ml-auto text-xs text-gray-500">
          Live telemetry synchronized every 30 seconds
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* SIDEBAR */}
        <div className="bg-[#0e1428] border border-cyan-500/10 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-cyan-500/10 font-semibold">
            Fleet Devices
          </div>

          <div className="max-h-[700px] overflow-y-auto">
            {groupedAgents.map((agent) => {
              const risk = getRiskBadge(agent.highestScore);

              return (
                <button
                  key={agent.agent_id}
                  onClick={() => setSelectedAgent(agent.agent_id)}
                  className={`w-full text-left p-4 border-b border-cyan-500/5 hover:bg-cyan-500/5 transition ${
                    selectedAgent === agent.agent_id
                      ? "bg-cyan-500/10"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getOSIcon(agent.os_type)}

                    <div className="font-medium truncate">
                      {agent.hostname}
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mb-2 truncate">
                    {agent.agent_id}
                  </div>

                  <div className="flex items-center justify-between">
                    <span
                      className={`px-2 py-1 rounded-lg text-[10px] font-semibold ${risk.className}`}
                    >
                      {risk.label}
                    </span>

                    <span className="text-xs text-gray-400">
                      {agent.rows.length} records
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* DETAILS */}
        <div className="lg:col-span-3 bg-[#0e1428] border border-cyan-500/10 rounded-2xl overflow-hidden">
          {!selectedAgentData ? (
            <div className="p-10 text-center text-gray-500">
              {loading
                ? "Loading telemetry..."
                : "No telemetry records found."}
            </div>
          ) : (
            <>
              {/* DEVICE HEADER */}
              <div className="p-6 border-b border-cyan-500/10">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {getOSIcon(selectedAgentData.os_type)}

                  <h2 className="text-2xl font-bold">
                    {selectedAgentData.hostname}
                  </h2>

                  <span className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs border border-emerald-500/30">
                    ONLINE
                  </span>
                </div>

                <div className="text-sm text-gray-500">
                  Agent ID: {selectedAgentData.agent_id}
                </div>
              </div>

              {/* RECORDS */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#11192f] text-gray-300 text-sm">
                    <tr>
                      <th className="p-4 text-left">Risk</th>
                      <th className="p-4 text-left">CPU</th>
                      <th className="p-4 text-left">RAM</th>
                      <th className="p-4 text-left">Network</th>
                      <th className="p-4 text-left">Processes</th>
                      <th className="p-4 text-left">IPs</th>
                      <th className="p-4 text-left">ML Score</th>
                      <th className="p-4 text-left">Time</th>
                    </tr>
                  </thead>

                  <tbody>
                    {selectedAgentData.rows.map((row, idx) => {
                      const risk = getRiskBadge(
                        row.ml_anomaly_score
                      );

                      return (
                        <tr
                          key={idx}
                          className="border-t border-cyan-500/5 hover:bg-cyan-500/5 transition"
                        >
                          <td className="p-4">
                            <span
                              className={`px-2 py-1 rounded-lg text-xs font-semibold ${risk.className}`}
                            >
                              {risk.label}
                            </span>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Cpu size={14} />
                              {row.cpu_avg?.toFixed(2)}%
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <MemoryStick size={14} />
                              {row.ram_avg?.toFixed(2)}%
                            </div>
                          </td>

                          <td className="p-4 text-sm">
                            <div>
                              TX: {formatBytes(row.network_tx_bytes)}
                            </div>

                            <div>
                              RX: {formatBytes(row.network_rx_bytes)}
                            </div>
                          </td>

                          <td className="p-4">
                            {row.new_processes_spawned}
                          </td>

                          <td className="p-4">
                            {row.unique_ips_contacted}
                          </td>

                          <td className="p-4">
                            <span className="font-mono text-cyan-400">
                              {Number(
                                row.ml_anomaly_score || 0
                              ).toFixed(4)}
                            </span>
                          </td>

                          <td className="p-4 text-sm text-gray-400">
                            {new Date(
                              row.recorded_at
                            ).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* AGI INSIGHTS */}
              <div className="border-t border-cyan-500/10 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle
                    className="text-orange-400"
                    size={18}
                  />

                  <h3 className="font-semibold">
                    AI Behavioral Intelligence
                  </h3>
                </div>

                <div className="space-y-3">
                  {selectedAgentData.rows
                    .filter((x) => x.agi_explanation)
                    .slice(0, 5)
                    .map((row, idx) => (
                      <div
                        key={idx}
                        className="bg-[#11192f] border border-cyan-500/10 rounded-xl p-4"
                      >
                        <div className="text-sm text-gray-300">
                          {row.agi_explanation}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}